import { useState } from "react";
import "./LightSignup.css";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import logo from "../../../assets/mooring-mate-logo.png";
import { Copyright } from "../../../components";
import { validateEmail, validatePassword } from "../../../utils";
import toast from "react-hot-toast";
import { useNavigate, Link } from "react-router-dom";
import { lightSignup } from "../../../services";

export const LightSignup = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [confirmPasswordError, setConfirmPasswordError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [remember, setRemember] = useState(false);

  const handleEmailBlur = () => {
    if (email && !validateEmail(email)) {
      setEmailError(true);
    } else {
      setEmailError(false);
    }
  };

  const handlePasswordBlur = () => {
    if (password && !validatePassword(password)) {
      setPasswordError(true);
    } else {
      setPasswordError(false);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear previous errors
    setConfirmPasswordError(false);

    // Validate before submission
    if (!validateEmail(email)) {
      setEmailError(true);
      toast.error("Please enter a valid email address");
      return;
    }

    if (!validatePassword(password)) {
      setPasswordError(true);
      toast.error("Password must meet the required criteria");
      return;
    }

    // Validate confirm password only on submit
    if (password !== confirmPassword) {
      setConfirmPasswordError(true);
      toast.error("Passwords do not match");
      return;
    }

    // Prepare payload with minimal required data
    const payload = {
      userName: email,
      password: password,
      imoHullNumber: "", // Has not to be send on registration as per updated requirements and is not strictly checked on the backend side, that's why passing an empty string to it to avoid any fatal errors on backend.
    };

    setLoading(true);
    const res = await lightSignup(payload);

    if (res?.status === "Error") {
      setLoading(false);
      if (res?.message?.hasOwnProperty("errors")) {
        toast.error(res?.message?.errors[0]?.message);
      } else {
        toast.error("Something went wrong, please try again later");
      }
      return;
    } else if (res?.status === false) {
      setLoading(false);
      toast.error(res?.message);
      return;
    }

    setLoading(false);
    if (res?.status === "Success") {
      toast.success(res?.message);

      const userData = {
        token: res?.token,
        data: {
          yachtName: res?.data?.yachtName || null,
          ...res?.data
        },
      };

      if (remember) {
        localStorage.setItem("dockingmate", JSON.stringify(userData));
      } else {
        sessionStorage.setItem("dockingmate", JSON.stringify(userData));
      }

      navigate("/home");
      return;
    }

    setLoading(false);
    toast.error("Something went wrong, please try again later");
    return;
  };

  return (
    <Grid container className="signupContainer">
      <Grid
        item
        xs={false}
        sm={4}
        md={6}
        lg={7}
        className="imageContainer"
      ></Grid>
      <Grid item xs={12} sm={8} md={6} lg={5} className="formContainer">
        <div className="logo-section">
          <img
            className="landing-logo"
            src={logo}
            alt="Logo"
            onClick={() => navigate("/")}
          />
        </div>
        <h1 className="title">Sign Up</h1>
        <form
          onSubmit={handleSubmit}
          style={{
            width: "100%",
          }}
        >
          <TextField
            variant="outlined"
            margin="normal"
            type="email"
            fullWidth
            size="small"
            id="email"
            required
            label="Email Address"
            name="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={handleEmailBlur}
            error={emailError}
            helperText={emailError && "Please enter a valid email address"}
          />
          <TextField
            variant="outlined"
            margin="normal"
            fullWidth
            name="password"
            size="small"
            required
            label="Password"
            type={showPassword ? "text" : "password"}
            id="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onBlur={handlePasswordBlur}
            error={passwordError}
            helperText={
              passwordError
                ? "Password must be at least 8 characters and include at least one uppercase, one lowercase, one digit and one special character."
                : ""
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            variant="outlined"
            margin="normal"
            fullWidth
            name="confirmPassword"
            size="small"
            required
            label="Confirm Password"
            type={showConfirmPassword ? "text" : "password"}
            id="confirmPassword"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              // Clear error when user starts typing
              if (confirmPasswordError) {
                setConfirmPasswordError(false);
              }
            }}
            error={confirmPasswordError}
            helperText={confirmPasswordError && "Passwords do not match"}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle confirm password visibility"
                    onClick={handleClickShowConfirmPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <FormControlLabel
            control={
              <Checkbox
                value={remember}
                onChange={(e) => setRemember(e.target.checked)}
                color="primary"
              />
            }
            label="Remember me"
          />
          <Button
            fullWidth
            type="submit"
            variant="contained"
            color="primary"
            className="theme-btn"
            disabled={loading}
            sx={{
              boxShadow: "0 0px 0px 0px rgba(255, 255, 255, .0)!important",
              height: "50px",
              marginBottom: "12px",
              borderRadius: "10px",
              marginTop: "24px",
            }}
          >
            {loading ? "Creating Account..." : "Sign Up"}
          </Button>
          <Grid container>
            <Grid item>
              <Link to="/login" variant="body2" className="link">
                {"Already have an account? Sign In"}
              </Link>
            </Grid>
          </Grid>
          <Box mt={5}>
            <Copyright />
          </Box>
        </form>
      </Grid>
    </Grid>
  );
};
